import type { EditorThemeClasses } from 'lexical';
import { useMemo } from 'react';

import { useTextEditorContext } from '../contexts/textEditor.context';
import { useTextEditorClasses } from './useTextEditorClasses';

interface UseTextEditorThemeParams {
  isEditing: boolean;
  /** Optional tenant-branded color for body text and headings.
   *
   *  Applied at the Lexical theme layer (i.e. baked into the
   *  generated `p`/`h*`/`li` class rules in `useTextEditorClasses`)
   *  rather than via a parent `sx` rule like `& *: inherit`.
   *
   *  The default theme sets explicit `color` on the rendered `<p>`/
   *  `<h*>`/`<li>` elements, so the only sx rule that can override it
   *  with equal-or-greater specificity is `& *` — which ties (0,1,0)
   *  with the class-level color and falls back to source order to
   *  decide a winner. Emotion/MUI's class injection order is NOT
   *  stable across mount paths (editor mount vs. read-only Markdown
   *  mount vs. print render), so an `sx`-based override applies
   *  sporadically: it works once, fails on a re-mount, comes back on
   *  a subsequent edit. Threading the color through the theme class
   *  itself removes the competing rule entirely. */
  bodyTextColor?: string | null;
  /** Optional tenant-branded body alignment, baked into the generated
   *  `p`/`h*`/`li` class rules alongside `bodyTextColor`. Absent leaves
   *  the default (left) alignment. */
  bodyTextAlignment?: 'left' | 'center' | 'right' | null;
}

export function useTextEditorTheme({
  isEditing,
  bodyTextColor,
  bodyTextAlignment,
}: UseTextEditorThemeParams) {
  const { theme: textEditorTheme } = useTextEditorContext();
  const { classes } = useTextEditorClasses({
    textEditorTheme,
    isEditing,
    bodyTextColor,
    bodyTextAlignment,
  });

  const theme = useMemo<EditorThemeClasses>(() => {
    return {
      heading: {
        h1: classes.h1,
        h2: classes.h2,
        h3: classes.h3,
        h4: classes.h4,
        h5: classes.h5,
        h6: classes.h6,
      },
      list: {
        ul: classes.listUl,
        listitem: classes.listListitem,
        listitemChecked: classes.listListitemChecked,
        listitemUnchecked: classes.listListitemUnchecked,
        nested: {
          listitem: classes.listNestedListitem,
        },
        olDepth: [
          classes.listOlDepth1,
          classes.listOlDepth2,
          classes.listOlDepth3,
          classes.listOlDepth4,
          classes.listOlDepth5,
        ],
      },
      paragraph: classes.p,
      quote: classes.blockquote,
      hr: classes.hr,
      table: classes.table,
      tableCell: classes.tableCell,
      tableCellHeader: classes.tableCellHeader,
      tableCellSelected: classes.tableCellSelected,
      tableSelection: classes.tableSelection,
      text: {
        bold: classes.bold,
        code: classes.code,
        italic: classes.italic,
        strikethrough: classes.strikethrough,
        subscript: classes.subscript,
        superscript: classes.superscript,
        underline: classes.underline,
        underlineStrikethrough: classes.underlineStrikethrough,
      },
    };
  }, [
    classes.blockquote,
    classes.bold,
    classes.code,
    classes.h1,
    classes.h2,
    classes.h3,
    classes.h4,
    classes.h5,
    classes.h6,
    classes.hr,
    classes.italic,
    classes.listUl,
    classes.listListitem,
    classes.listListitemChecked,
    classes.listListitemUnchecked,
    classes.listNestedListitem,
    classes.listOlDepth1,
    classes.listOlDepth2,
    classes.listOlDepth3,
    classes.listOlDepth4,
    classes.listOlDepth5,
    classes.p,
    classes.strikethrough,
    classes.subscript,
    classes.superscript,
    classes.table,
    classes.tableCell,
    classes.tableCellHeader,
    classes.tableCellSelected,
    classes.tableSelection,
    classes.underline,
    classes.underlineStrikethrough,
  ]);

  return theme;
}
